import { Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { GameStoreDispatcher } from 'src/app/core/state/game/game.dispatcher';
import { Observable, Subscription, of, withLatestFrom } from 'rxjs';
import { AuthStoreDispatcher } from 'src/app/core/state/auth/auth.dispatcher';
import { map, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { GameStateId } from 'src/app/core/state/game/game.state';
import { SplitScreenService } from 'src/app/game/splitScreen.service';
import { GameSocketStoreDispatcher } from 'src/app/core/state/gameSocket/gameSocket.dispatcher';
import { ApiService } from 'src/app/core/backend/api.service';
import { TableType } from '@soposopo/sopo-models';

@Component({
    selector: 'app-tab-box',
    templateUrl: './tab-box.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./tab-box.component.scss'],
    standalone: false
})
export class TabBoxComponent implements OnInit, OnDestroy {
  @Input() tableID: string;
  @Input() gameStateId: GameStateId;
  @Input() inAppGameBox = false;
  @Input() tableType: TableType;
  @Input() tournamentId: string;
  @Input() clubId: string;

  clientID: string;
  timePerTurn: number = 15;

  player$: Observable<{hand: string[], isFolded: boolean}> = of({hand: [], isFolded: false});
  closeGameSubscription: Subscription;
  gameServerSubscription: Subscription;
  socketSubscription: Subscription;
  socketConnectionSubscription: Subscription;

  constructor(
    private gameDispatcher: GameStoreDispatcher,
    private authService: AuthStoreDispatcher,
    private ref: ChangeDetectorRef,
    private splitScreenService: SplitScreenService,
    private gameSocketDispatcher: GameSocketStoreDispatcher,
    ) { }

  async ngOnInit() {
    this.clientID = await this.authService.getMyID();
    this.player$ = this.gameDispatcher.getDealer(this.gameStateId).pipe(
      map(dealer => {
        const player: {hand: string[], isFolded: boolean} = {hand: [], isFolded: false};
        if (dealer.players[this.clientID]){
          dealer.players[this.clientID].hand.forEach(hand => {
            player.hand.push(hand.card);
          });
          player.isFolded = !dealer.players[this.clientID].isInPlay;
        }
        return player;
      })
    );

    if (this.inAppGameBox) {
        this.socketConnectionSubscription = this.gameSocketDispatcher.getGameSocketStatus(this.gameStateId)
        .pipe(distinctUntilChanged((prev, curr) => prev === curr))
        .subscribe(socketConnected => {
          if (socketConnected === 'disconnected') {
            this.gameDispatcher.resetDealer(this.tableID);
          }
          this.ref.detectChanges();
        });
        this.connectToDealer();
        this.closeGameSubscription = this.gameDispatcher.getGameIsDeleted(this.gameStateId).pipe(distinctUntilChanged())
            .subscribe(deleted => {
                if (!deleted) return;
                this.closeGameSubscription.unsubscribe();
                this.splitScreenService.closeGame(this.gameStateId);
            }
        )
    }

    this.ref.detectChanges();
  }

  connectToDealer() {
    this.gameDispatcher.loadGame(this.tableID, this.tournamentId, this.tableType, this.gameStateId, this.clientID);

    this.gameServerSubscription = this.gameDispatcher.getTableServer(this.gameStateId).pipe(
      withLatestFrom(this.gameSocketDispatcher.getGameSocketStateObservable()),
    ).subscribe(
      ([gameServer, socketState]) => {
        const disconnect = () => {
          this.gameSocketDispatcher.removeSocket(this.gameStateId);
        }

        if (gameServer.state === 'UnAllocated') return disconnect();

        const url = ApiService.resolveGameServerUrl(gameServer);
        const stateUrl = socketState.sockets.find(socket => socket.gameStateId === this.gameStateId)?.url;

        if (gameServer.state === 'Allocated' && stateUrl !== undefined && stateUrl !== url) {
          disconnect();
        }

        this.gameSocketDispatcher.createSocket(this.gameStateId, url, this.tableType);
      }
    );

    this.socketSubscription = this.gameSocketDispatcher.getGameSocketStatusAndId(this.gameStateId).pipe(
        filter(({ connectionStatus }) => connectionStatus === 'connected'),
        switchMap(() => this.gameSocketDispatcher.getGameSocketAsPromise(this.gameStateId))
    ).subscribe(async (socket) => {
      if(socket !== undefined) {
        socket.connect();
        await socket.enterRoom(this.tableID, this.clubId);
        socket.initiateGameState(this.tableID);
        socket.setGameState(this.tableID, this.gameStateId, this.clientID);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.closeGameSubscription) this.closeGameSubscription.unsubscribe();
    if (this.gameServerSubscription) this.gameServerSubscription.unsubscribe();
    if (this.socketSubscription) this.socketSubscription.unsubscribe();
    if (this.socketConnectionSubscription) this.socketConnectionSubscription.unsubscribe();
  }

}
